import { useLiveQuery } from 'dexie-react-hooks';
import { Search } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { addMonths, type EventType } from '@dairy/shared';
import { Card, EmptyState, Input, PageHeader, buttonVariants } from '@/components/ui';
import { db, getMeta } from '@/db/db';
import { useHerd, useToday, type AnimalRow } from '@/db/hooks';
import { cn } from '@/lib/cn';
import { ReproBadge, StageBadge } from '@/features/animals/common';
import { EventForm } from './EventForm';

const BREEDING: EventType[] = ['HEAT', 'SERVICE', 'PREG_CHECK', 'CALVING', 'DRY_OFF', 'ABORTION'];
const HEALTH: EventType[] = ['TREATMENT', 'VACCINATION', 'DEWORMING', 'WEIGHT', 'NOTE'];

/** S3-T04: the types offered for an animal (breeding types for females from 9 months; DRY_OFF only when milking). */
export function validTypes(row: AnimalRow, today: string): EventType[] {
  const a = row.animal;
  const types: EventType[] = [];
  if (a.sex === 'FEMALE' && today >= addMonths(a.birthDate, 9)) {
    for (const ty of BREEDING) {
      if (ty === 'DRY_OFF' && row.stage !== 'MILKING') continue;
      if (ty === 'ABORTION' && row.repro?.status === 'OPEN') continue;
      types.push(ty);
    }
  }
  return [...types, ...HEALTH];
}

/** Screen 10 — Record event: animal picker → type picker → the type's form (S3-T04). Also edits an event. */
export function RecordEventPage() {
  const { eventId } = useParams();
  const [params, setParams] = useSearchParams();
  const { t } = useTranslation();
  const today = useToday();
  const herd = useHerd(today);
  const existing = useLiveQuery(() => (eventId ? db.events.get(eventId) : undefined), [eventId]);
  const animalId = existing?.animalId ?? params.get('animalId') ?? '';
  const type = (existing?.type ?? params.get('type') ?? '') as EventType | '';
  const row = herd.byId.get(animalId);

  if (eventId && !existing) return null;
  if (!animalId || !row) {
    return (
      <div>
        <PageHeader title={t('events.chooseAnimal')} />
        <AnimalPicker rows={herd.rows} onPick={(id) => setParams({ ...Object.fromEntries(params), animalId: id })} />
      </div>
    );
  }
  if (!type) {
    const types = validTypes(row, today);
    return (
      <div>
        <PageHeader title={t('events.chooseType', { tag: row.animal.tag })} />
        <div className="mb-3 flex gap-2">
          <StageBadge stage={row.stage} />
          <ReproBadge status={row.repro?.status} />
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {types.map((ty) => (
            <button
              key={ty}
              onClick={() => setParams({ ...Object.fromEntries(params), animalId, type: ty })}
              className={cn(buttonVariants({ variant: 'secondary', size: 'lg' }), 'h-16')}
            >
              {t(`events.type.${ty}`)}
            </button>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to="/events/batch?type=VACCINATION" className={buttonVariants({ variant: 'ghost', size: 'sm' })}>
            {t('events.batchVaccination')}
          </Link>
          <Link to="/events/batch?type=DEWORMING" className={buttonVariants({ variant: 'ghost', size: 'sm' })}>
            {t('events.batchDeworming')}
          </Link>
        </div>
      </div>
    );
  }
  return <EventForm key={`${animalId}-${type}-${eventId ?? 'new'}`} row={row} type={type} existing={existing} protocolId={params.get('protocolId')} />;
}

function AnimalPicker({ rows, onPick }: { rows: AnimalRow[]; onPick: (id: string) => void }) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const recent = useLiveQuery(() => getMeta<string[]>('recentAnimals'), []) ?? [];
  const active = rows.filter((r) => r.animal.status === 'ACTIVE');
  const needle = q.trim().toLowerCase();
  const filtered = active
    .filter((r) => !needle || r.animal.tag.toLowerCase().includes(needle) || (r.animal.name ?? '').toLowerCase().includes(needle))
    .sort((a, b) => {
      const ra = recent.indexOf(a.animal.id);
      const rb = recent.indexOf(b.animal.id);
      if (ra !== rb) return (ra === -1 ? 1e9 : ra) - (rb === -1 ? 1e9 : rb);
      return 0;
    });
  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-3.5 h-5 w-5 text-stone-400" />
        <Input autoFocus className="pl-10" placeholder={t('animals.search')} value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {filtered.length === 0 ? <EmptyState>{t('animals.empty')}</EmptyState> : null}
      <div className="grid gap-2">
        {filtered.map((r) => (
          <Card key={r.animal.id} className="p-0">
            <button onClick={() => onPick(r.animal.id)} className="flex min-h-14 w-full items-center gap-3 px-3 text-left">
              <span className="flex-1 font-bold">
                {r.animal.tag} <span className="font-normal text-stone-600">{r.animal.name ?? ''}</span>
              </span>
              <StageBadge stage={r.stage} />
            </button>
          </Card>
        ))}
      </div>
    </div>
  );
}
